/**
 * Direct HTTPS invocation of a Cloud Functions onCall handler, bypassing
 * `firebase.functions().httpsCallable()`.
 *
 * The v7 namespaced firebase JS SDK in this project has an intermittent
 * failure mode where `httpsCallable(name)({...})` throws a generic
 * `code: 'internal'` HttpsError *before* it ever issues the network
 * request — likely an auth-token-resolution race we've never been able
 * to reproduce from outside, but consistently bites cold callable
 * invocations after deploys. The same workaround already lives inline
 * in src/components/admin/Admin.vue (getAdminUsageStats) and
 * src/store/mapActions.js (getRasterTileSource); this helper exists
 * so any component that needs to call a Cloud Function can do it
 * without re-implementing the same envelope shape.
 *
 * Wire-protocol notes:
 *   request body:  `{ data: { ...payload } }`
 *   success body:  `{ result: { ...returned } }`
 *   error body:    `{ error: { message, status, details? } }`
 *
 * The server-side `functions.https.onCall(...)` handler is identical
 * regardless of whether the client used `httpsCallable` or this direct
 * fetch — both speak the same envelope.
 *
 * @param {string} name     Cloud Function name (e.g. 'inviteCollaborator').
 * @param {object} payload  JSON-serializable data the handler will see
 *                          as its first argument.
 * @returns {Promise<*>}    The handler's return value (already unwrapped
 *                          from the `result` envelope).
 * @throws {Error}          With message set to the server-side error
 *                          message when the function returns a structured
 *                          error, or `HTTP {status}: {body}` otherwise.
 */
const fb = require('@/firebaseConfig.js')

const FUNCTION_BASE = 'https://us-central1-efficient-vineyard.cloudfunctions.net'

async function callCallable (name, payload) {
  const user = fb.auth.currentUser
  if (!user) throw new Error('Sign in required.')
  const idToken = await user.getIdToken()
  const resp = await fetch(`${FUNCTION_BASE}/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + idToken
    },
    body: JSON.stringify({ data: payload || {} })
  })
  if (!resp.ok) {
    const errBody = await resp.text()
    let msg = `HTTP ${resp.status}`
    try {
      const parsed = JSON.parse(errBody)
      if (parsed && parsed.error) {
        msg = parsed.error.message || parsed.error.status || JSON.stringify(parsed.error)
      } else {
        msg += `: ${errBody.slice(0, 200)}`
      }
    } catch (e) {
      msg += `: ${errBody.slice(0, 200)}`
    }
    if (resp.status === 404) {
      msg = `${name} is not deployed. Run \`firebase deploy --only functions:${name}\` to publish it.`
    }
    throw new Error(msg)
  }
  const json = await resp.json()
  return json.result
}

module.exports = { callCallable }
